import { api, type DownloadedFile } from '@/lib/api';

export type SummaryFormat = 'pdf' | 'txt';

const ACCEPT: Record<SummaryFormat, string> = { pdf: 'application/pdf', txt: 'text/plain' };

function fetchSummary(applicationId: string, format: SummaryFormat): Promise<DownloadedFile> {
  return api.download(`/api/applications/${applicationId}/summary?format=${format}`, ACCEPT[format]);
}

/** Saves the summary as a file, as a link to it would if a link could carry the session. */
export async function downloadSummary(applicationId: string, format: SummaryFormat): Promise<void> {
  const { blob, fileName } = await fetchSummary(applicationId, format);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName ?? `application-summary.${format}`;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked after the click has been handed to the browser's download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Puts the text summary on the clipboard: the server's text, so what is pasted is
 * what is downloaded (A7). The clipboard item is given the pending text at once,
 * because Safari allows a clipboard write only within the click that asked for it.
 */
export async function copySummary(applicationId: string): Promise<void> {
  const text = fetchSummary(applicationId, 'txt').then((f) => f.blob.text());
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const blob = text.then((t) => new Blob([t], { type: 'text/plain' }));
    await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blob })]);
    return;
  }
  await navigator.clipboard.writeText(await text);
}
